import { BrokeragePricing } from '../../../slices/marketplace/pricing/models/brokerage-pricing.model';
import { BrokerageType } from '../../../slices/marketplace/pricing/models/brokerage-type.enum';
import { MarketplacePricing } from '../../../slices/marketplace/pricing/models/marketplace-pricing.model';
import { TransactionFees } from '../../../slices/marketplace/pricing/models/transaction-fees.model';
import { CurrencyFormatter } from '../../../utils/currencyFormatter';
import {
    ComingSoonCommercialTermsTableData,
    CommingSoonCommercialTermsRow,
} from '../RequestPreOrderEngagementAgreementModal/Models/Quote';

export class ComingSoonCommercialTermsCalculator {
    static calculateCommercialTerms(
        fteW2Dvms: number,
        ltmRevenue: number,
        type: BrokerageType,
        marketplacePricing: MarketplacePricing
    ): ComingSoonCommercialTermsTableData {
        ltmRevenue = Number(ltmRevenue.toString().replace(/,/g, ''));
        fteW2Dvms = Number(fteW2Dvms);
        const pricing =
            type === BrokerageType.Discount
                ? marketplacePricing.discountBrokeragePricing
                : marketplacePricing.fullServiceBrokeragePricing;
        const transactionFees = pricing.transactionFees;
        return {
            transactionFee: this.calculateTxFee(ltmRevenue, pricing),
            processFees: [
                this.calculateIdNdaFee(fteW2Dvms, transactionFees),
                this.calculateDataNdaFee(fteW2Dvms, transactionFees),
                this.calculateFinancialModelFee(fteW2Dvms, transactionFees),
                this.calculateBuyerMeetingCredit(transactionFees, type),
            ],
            otherFees: [
                this.calculateApplicationFee(fteW2Dvms, transactionFees),
                ...(type === BrokerageType.FullService
                    ? [
                          this.calculateMonthlyRetainerFee(
                              fteW2Dvms,
                              transactionFees
                          ),
                      ]
                    : []),
                this.calculateLateFee(fteW2Dvms, transactionFees),
                this.calculateSuspensionFee(fteW2Dvms, transactionFees, type),
                this.calculateTempoChangeFee(fteW2Dvms, transactionFees),
            ],
            tailPeriod: `${pricing.transactionFees.tailPeriodMonths} Months`,
        };
    }

    private static calculateTxFee(
        ltmRevenue: number,
        pricing: BrokeragePricing
    ): number {
        const transactionFees = pricing.transactionFeeSchedule;
        let targetFee = transactionFees.find((f) => f.revenueMax >= ltmRevenue);
        targetFee = targetFee
            ? targetFee
            : transactionFees[transactionFees.length - 1];
        return targetFee.feePercent;
    }

    private static calculateIdNdaFee(
        fteW2Dvms: number,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const price = CurrencyFormatter.formatToDollarValue(
            fteW2Dvms * fees.idNdaFeePerBuyer
        );
        return {
            id: 1,
            label: 'Buyer ID NDA Fee',
            toolTip:
                "Once a buyer signs an ID NDA with the seller, the seller's indentity is disclosed to that buyer. The seller selects which of the interested buyers it wishes to send an ID NDA. The seller is then charged ONLY for the number of buyers that actually elect to sign the ID NDA.",
            feeAmountValue: `${price} per Buyer (first ${fees.idNdaFirstXCount} count as 1)`,
            refundable: fees.idNdaFeeRefundable,
            creditable: fees.idNdaFeeCreditable,
        };
    }

    private static calculateDataNdaFee(
        fteW2Dvms: number,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const price = CurrencyFormatter.formatToDollarValue(
            fteW2Dvms * fees.dataNdaFeePerBuyer
        );
        return {
            id: 2,
            label: 'PIP/PIQ Provision Fee',
            toolTip:
                'Buyers must sign a Data NDA before they are eligible to receive either an practice investment presentation ("PIP") or the detailed financial model. The seller selects which of the interested buyers it wishes to send a Data NDA. The seller is then charged ONLY for the number of buyers that actually elect to sign the Data NDA.',
            feeAmountValue: `${price} per Buyer (first ${fees.dataNdaFirstXCount} count as 1)`,
            refundable: fees.dataNdaFeeRefundable,
            creditable: fees.dataNdaFeeCreditable,
        };
    }

    private static calculateFinancialModelFee(
        fteW2Dvms: number,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const price = CurrencyFormatter.formatToDollarValue(
            fteW2Dvms * fees.financialModelFeePerBuyer
        );
        return {
            id: 3,
            label: 'DFM Provision Fee',
            toolTip:
                'The seller selects which of the interested buyers it wishes to allow to see the detailed financial model. The seller is then charged for each of these buyers which view the detailed financial model.',
            feeAmountValue: `${price} per Buyer (first ${fees.financialModelFirstXCount} count as 1)`,
            refundable: fees.financialModelFeeRefundable,
            creditable: fees.financialModelFeeCreditable,
        };
    }

    private static calculateBuyerMeetingCredit(
        fees: TransactionFees,
        brokerageType: BrokerageType
    ): CommingSoonCommercialTermsRow {
        const virtualMeetingCredit = CurrencyFormatter.formatToDollarValue(
            fees.buyerMeetingVirtualCredit
        );
        const inPersonMeetingCredit = CurrencyFormatter.formatToDollarValue(
            fees.buyerMeetingInPersonCredit
        );
        return {
            id: 4,
            label: 'Buyer Meeting Credits',
            toolTip:
                "Buyers are charged a refundable meeting fee for either an in-person or virtual meeting with the seller. This fee is refunded to the buyer if the seller is unable or unwilling to attend the meeting. Meetings are typically 3-5 hours in length and may include discussion over a meal. There is no meeting fee for the seller. VetValue credits 50% of the buyer's meeting fee towards the seller's transaction fee. ",
            feeAmountValue: `${virtualMeetingCredit} credit per virtual meeting \n ${inPersonMeetingCredit} credit per in-person meeting`,
            feeAmountValueTooltip:
                brokerageType === BrokerageType.FullService
                    ? 'Net of advisor travel expenses'
                    : undefined,
            refundable: fees.buyerMeetingRefundable,
            creditable: fees.buyerMeetingCreditable,
        };
    }

    private static calculateApplicationFee(
        fteW2Dvms: number,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const totalPrice =
            fees.applicationFee + fees.applicationFeePerDvm * fteW2Dvms;
        const price = CurrencyFormatter.formatToDollarValue(totalPrice);
        return {
            id: 5,
            label: 'Application Fee',
            toolTip:
                'Charged at the time of application to engage this platform',
            feeAmountValue: `${price}`,
            refundable: fees.applicationFeeRefundable,
            creditable: fees.applicationFeeCreditable,
        };
    }

    private static calculateMonthlyRetainerFee(
        fteW2Dvms: number,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const totalPrice =
            fees.monthlyRetainerFee + fees.monthlyRetainerPerDvm * fteW2Dvms;
        const price = CurrencyFormatter.formatToDollarValue(totalPrice);
        return {
            id: 6,
            label: 'Monthly Engagement Fee',
            toolTip:
                'An engagement fee is charged at the beginning of each month, starting at the effective date of the engagement. During any period for which the seller elects to suspend the engagement, this fee is reduce by 50% for the duration of that suspendion.',
            feeAmountValue: `${price}  per month`,
            refundable: fees.monthlyRetainerFeeRefundable,
            creditable: fees.monthlyRetainerFeeCreditable,
        };
    }

    private static calculateLateFee(
        fteW2Dvms: number,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const firstOccurrence = CurrencyFormatter.formatToDollarValue(
            fees.lateFeeFirstOccurrence * fteW2Dvms
        );
        const additionalOccurrence = CurrencyFormatter.formatToDollarValue(
            fees.lateFeeAdditionalOccurrence * fteW2Dvms
        );
        return {
            id: 7,
            label: 'Late Fee',
            toolTip:
                "Late fees are charged each time the seller does not complete a step by that step's deadline.",
            feeAmountValue: `${firstOccurrence} per 1st occurrence, \n ${additionalOccurrence} for each additional occurrence (days late)`,
            refundable: fees.lateFeeRefundable,
            creditable: fees.lateFeeCreditable,
        };
    }

    private static calculateSuspensionFee(
        fteW2Dvms: number,
        fees: TransactionFees,
        brokerageType: BrokerageType
    ): CommingSoonCommercialTermsRow {
        const totalPrice =
            fees.suspensionFee + fees.suspensionFeePerDvm * fteW2Dvms;
        const price = CurrencyFormatter.formatToDollarValue(totalPrice);

        const suspensionFeeTooltip =
            brokerageType === BrokerageType.FullService
                ? 'Suspension fees begin on the retainer renewal date. During suspension, the suspension fee replaces the monthly retainer. When the engagement resumes, regular retainer fees restart immediately, and the renewal date is reset to the resumption date.'
                : 'A suspension fee is charged for each month the engagement has been suspended by the seller.';

        return {
            id: 8,
            label: 'Monthly Suspension Fee',
            toolTip: suspensionFeeTooltip,
            feeAmountValue: `${price} per month`,
            refundable: fees.suspensionFeeRefundable,
            creditable: fees.suspensionFeeCreditable,
        };
    }

    private static calculateTempoChangeFee(
        fteW2Dvms: number,
        fees: TransactionFees
    ): CommingSoonCommercialTermsRow {
        const price = CurrencyFormatter.formatToDollarValue(
            fees.tempoChangeFeePerDvm * fteW2Dvms
        );
        return {
            id: 9,
            label: 'Tempo Change Fee',
            toolTip: undefined,
            feeAmountValue: `${price}`,
            refundable: fees.tempoChangeFeeRefundable,
            creditable: fees.tempoChangeFeeCreditable,
        };
    }
}
